import {
  matchesSearch,
  type RuntimeFlagsPayload,
  type RuntimeVar,
  type RuntimeVarClassFilter,
} from "./runtimeFlags";

export type EnvironmentComparisonStatus =
  | "different"
  | "same"
  | "only-stg"
  | "only-staging"
  | "protected";

export type EnvironmentComparisonFilter =
  | "attention"
  | "all"
  | EnvironmentComparisonStatus;

export type EnvironmentComparisonRow = {
  key: string;
  serviceId: string;
  serviceLabel: string;
  container?: string;
  name: string;
  stg?: RuntimeVar;
  staging?: RuntimeVar;
  status: EnvironmentComparisonStatus;
  protectedMetadataDiffers: boolean;
};

export type EnvironmentComparisonSummary = Record<
  EnvironmentComparisonStatus,
  number
>;

type FlatRuntimeVar = {
  serviceId: string;
  serviceLabel: string;
  entry: RuntimeVar;
};

export function buildEnvironmentComparison(
  stg: RuntimeFlagsPayload,
  staging: RuntimeFlagsPayload,
): EnvironmentComparisonRow[] {
  const stgRows = flatten(stg);
  const stagingRows = flatten(staging);
  const keys = new Set([...stgRows.keys(), ...stagingRows.keys()]);

  return [...keys]
    .map((key) => {
      const production = stgRows.get(key);
      const engineering = stagingRows.get(key);
      const protectedMetadataDiffers =
        production?.entry.class === "secret" &&
        engineering?.entry.class === "secret" &&
        production.entry.store !== engineering.entry.store;
      return {
        key,
        serviceId: production?.serviceId ?? engineering?.serviceId ?? "",
        serviceLabel:
          production?.serviceLabel ?? engineering?.serviceLabel ?? "Unknown",
        container: production?.entry.container ?? engineering?.entry.container,
        name: production?.entry.name ?? engineering?.entry.name ?? "",
        stg: production?.entry,
        staging: engineering?.entry,
        status: compareEntries(production?.entry, engineering?.entry),
        protectedMetadataDiffers,
      } satisfies EnvironmentComparisonRow;
    })
    .sort(
      (left, right) =>
        left.serviceLabel.localeCompare(right.serviceLabel) ||
        left.name.localeCompare(right.name) ||
        (left.container ?? "").localeCompare(right.container ?? ""),
    );
}

export function summarizeEnvironmentComparison(
  rows: EnvironmentComparisonRow[],
): EnvironmentComparisonSummary {
  const summary: EnvironmentComparisonSummary = {
    different: 0,
    same: 0,
    "only-stg": 0,
    "only-staging": 0,
    protected: 0,
  };
  for (const row of rows) summary[row.status] += 1;
  return summary;
}

export function filterEnvironmentComparison(
  rows: EnvironmentComparisonRow[],
  search: string,
  classFilter: RuntimeVarClassFilter,
  comparisonFilter: EnvironmentComparisonFilter,
) {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    const classMatches =
      classFilter === "all" ||
      row.stg?.class === classFilter ||
      row.staging?.class === classFilter;
    if (!classMatches) return false;

    const comparisonMatches =
      comparisonFilter === "all" ||
      (comparisonFilter === "attention"
        ? ["different", "only-stg", "only-staging"].includes(row.status) ||
          row.protectedMetadataDiffers
        : row.status === comparisonFilter);
    if (!comparisonMatches) return false;

    if (!needle) return true;
    if (
      row.name.toLowerCase().includes(needle) ||
      row.serviceLabel.toLowerCase().includes(needle) ||
      row.serviceId.toLowerCase().includes(needle) ||
      row.container?.toLowerCase().includes(needle)
    ) {
      return true;
    }
    return Boolean(
      (row.stg && matchesSearch(row.stg, needle)) ||
        (row.staging && matchesSearch(row.staging, needle)),
    );
  });
}

function flatten(payload: RuntimeFlagsPayload) {
  const rows = new Map<string, FlatRuntimeVar>();
  for (const service of payload.services) {
    for (const entry of service.vars) {
      const key = comparisonKey(service.id, entry);
      rows.set(key, {
        serviceId: service.id,
        serviceLabel: service.label,
        entry,
      });
    }
  }
  return rows;
}

function comparisonKey(serviceId: string, entry: RuntimeVar) {
  return [serviceId, entry.container ?? "", entry.name].join("\u0000");
}

function compareEntries(
  stg?: RuntimeVar,
  staging?: RuntimeVar,
): EnvironmentComparisonStatus {
  if (!stg) return "only-staging";
  if (!staging) return "only-stg";
  if (stg.class === "secret" && staging.class === "secret") {
    return "protected";
  }
  if (stg.class !== staging.class) return "different";
  if (stg.class === "flag") {
    return stg.enabled === staging.enabled ? "same" : "different";
  }
  return (stg.value ?? "") === (staging.value ?? "") ? "same" : "different";
}
